/* AfoScrub - shared scroll-scrub runtime for the Afghani Oil Pipette and
 * Root-to-Strand sequences. The asset is loaded once at route level and owns
 * one event/listener set for every declarative canvas on the page.
 *
 * Contract:
 *   <img data-scrub-poster="key" src="...poster.webp">
 *   <canvas data-scrub="key"
 *           data-scrub-base="...prefix-"
 *           data-scrub-count="64"
 *           data-scrub-drive="drop|coil|stage"
 *           data-scrub-media="(min-width: 900px)">
 */
(function () {
  'use strict';

  var VERSION = '2.0.0';
  var existing = window.AfoScrub;

  /* A second execution can happen while the Theme Editor is refreshing
   * assets. Reuse the live manager instead of installing another listener set. */
  if (existing && existing.version === VERSION && typeof existing.scan === 'function') {
    existing.scan(document);
    return;
  }

  var sequenceRegistry = new Map();
  var framePools = new Map();
  var observedRoots = new Map();
  var rootProximity = new Map();
  var reducedQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  var reduced = !!(reducedQuery && reducedQuery.matches);
  var runtimeController = null;
  var resizeObserver = null;
  var intersectionObserver = null;
  var animationFrame = 0;
  var orientationTimer = 0;
  var quoteTimer = 0;
  var POOL_IDLE_MS = 15000;
  var SEQUENCE_IDLE_MS = 30000;

  function clamp(value) {
    return value < 0 ? 0 : value > 1 ? 1 : value;
  }

  var PROGRESS = {
    coil: function (element, viewportHeight) {
      var rect = element.getBoundingClientRect();
      return clamp((viewportHeight * 0.72 - rect.top) / (rect.height + viewportHeight * 0.3));
    },
    drop: function (element, viewportHeight) {
      var rect = element.getBoundingClientRect();
      return clamp((viewportHeight * 0.8 - rect.top) / (rect.height + viewportHeight * 0.22));
    },
    stage: function (element, viewportHeight) {
      var stage = null;
      var candidates = element.querySelectorAll('[data-stage]');
      for (var i = 0; i < candidates.length; i += 1) {
        if (candidates[i].offsetWidth > 0) {
          stage = candidates[i];
          break;
        }
      }
      var rect = (stage || element).getBoundingClientRect();
      return clamp((viewportHeight * 0.85 - rect.top) / (rect.height + viewportHeight * 0.3));
    }
  };

  function buildAnchorOrder(count) {
    var order = [];
    for (var i = 0; i < count; i += 8) order.push(i);
    if (order[order.length - 1] !== count - 1) order.push(count - 1);
    return order;
  }

  function poolKey(base, count) {
    return base + '|' + count;
  }

  function FramePool(base, count) {
    this.base = base;
    this.count = count;
    this.key = poolKey(base, count);
    this.frames = new Array(count);
    this.states = new Array(count).fill(0);
    this.attempts = new Array(count).fill(0);
    this.queue = buildAnchorOrder(count);
    this.subscribers = new Set();
    this.pendingImages = new Map();
    this.failures = new Set();
    this.inflight = 0;
    this.maxInflight = 6;
    this.retainRadius = 6;
    this.drawRadius = 3;
    this.generation = 1;
    this.evictionTimer = 0;
    this.disposed = false;
  }

  FramePool.prototype.acquire = function (sequence) {
    if (this.disposed) return;
    if (this.evictionTimer) {
      window.clearTimeout(this.evictionTimer);
      this.evictionTimer = 0;
    }
    this.subscribers.add(sequence);
  };

  FramePool.prototype.release = function (sequence) {
    this.subscribers.delete(sequence);
    this.evictFarFrames();
    if (this.subscribers.size || this.evictionTimer || this.disposed) return;
    var self = this;
    this.evictionTimer = window.setTimeout(function () {
      self.evictionTimer = 0;
      if (!self.subscribers.size) self.dispose();
    }, POOL_IDLE_MS);
  };

  FramePool.prototype.request = function (index) {
    if (this.disposed || !this.subscribers.size) return;
    index = Math.max(0, Math.min(this.count - 1, index));
    var priority = [];
    for (var distance = 0; distance <= this.retainRadius; distance += 1) {
      var before = index - distance;
      var after = index + distance;
      if (before >= 0 && this.states[before] === 0) priority.push(before);
      if (distance && after < this.count && this.states[after] === 0) priority.push(after);
    }
    this.queue = priority.concat(this.queue);
    this.trimQueue();
    this.evictFarFrames();
    this.pump();
  };

  FramePool.prototype.isAnchor = function (index) {
    return index === this.count - 1 || index % 8 === 0;
  };

  FramePool.prototype.shouldRetain = function (index) {
    if (this.isAnchor(index)) return true;
    var keep = false;
    this.subscribers.forEach(function (sequence) {
      if (sequence.pending >= 0 && Math.abs(sequence.pending - index) <= this.retainRadius) keep = true;
    }, this);
    return keep;
  };

  FramePool.prototype.trimQueue = function () {
    var self = this;
    var seen = {};
    this.queue = this.queue.filter(function (index) {
      if (index < 0 || index >= self.count || seen[index] || self.states[index] !== 0 || !self.shouldRetain(index)) {
        return false;
      }
      seen[index] = true;
      return true;
    });
  };

  FramePool.prototype.evictFarFrames = function () {
    for (var index = 0; index < this.count; index += 1) {
      if (this.states[index] === 2 && !this.shouldRetain(index)) {
        this.frames[index] = null;
        this.states[index] = 0;
      }
    }
  };

  FramePool.prototype.pump = function () {
    if (this.disposed || !this.subscribers.size) return;
    var self = this;

    while (this.inflight < this.maxInflight && this.queue.length) {
      var index = this.queue.shift();
      if (this.states[index] !== 0) continue;

      this.states[index] = 1;
      this.attempts[index] += 1;
      this.inflight += 1;

      (function (frameIndex, generation) {
        var image = new Image();
        image.decoding = 'async';
        self.pendingImages.set(frameIndex, image);

        image.onload = function () {
          if (self.disposed || generation !== self.generation) return;
          self.pendingImages.delete(frameIndex);
          self.inflight = Math.max(0, self.inflight - 1);
          if (self.shouldRetain(frameIndex)) {
            self.frames[frameIndex] = image;
            self.states[frameIndex] = 2;
            self.subscribers.forEach(function (sequence) {
              sequence.onFrameReady();
            });
          } else {
            self.states[frameIndex] = 0;
          }
          self.pump();
        };

        image.onerror = function () {
          if (self.disposed || generation !== self.generation) return;
          self.pendingImages.delete(frameIndex);
          self.inflight = Math.max(0, self.inflight - 1);

          if (self.attempts[frameIndex] < 2 && self.subscribers.size) {
            self.states[frameIndex] = 0;
            window.setTimeout(function () {
              if (self.disposed || generation !== self.generation || !self.subscribers.size) return;
              self.queue.unshift(frameIndex);
              self.pump();
            }, 250);
          } else {
            self.states[frameIndex] = 3;
            self.failures.add(frameIndex);
            if (window.console && typeof window.console.warn === 'function') {
              window.console.warn('AfoScrub failed to load frame', self.base + ('00' + frameIndex).slice(-3) + '.webp');
            }
            self.pump();
          }
        };

        image.src = self.base + ('00' + frameIndex).slice(-3) + '.webp';
      })(index, this.generation);
    }
  };

  FramePool.prototype.nearest = function (index) {
    if (this.states[index] === 2) return index;
    for (var distance = 1; distance <= this.drawRadius; distance += 1) {
      if (index - distance >= 0 && this.states[index - distance] === 2) return index - distance;
      if (index + distance < this.count && this.states[index + distance] === 2) return index + distance;
    }
    return -1;
  };

  FramePool.prototype.dispose = function () {
    if (this.disposed) return;
    this.disposed = true;
    this.generation += 1;
    if (this.evictionTimer) window.clearTimeout(this.evictionTimer);
    this.evictionTimer = 0;

    this.pendingImages.forEach(function (image) {
      image.onload = null;
      image.onerror = null;
    });
    this.pendingImages.clear();
    this.subscribers.clear();
    this.frames.fill(null);
    this.states.fill(0);
    this.queue.length = 0;
    this.inflight = 0;
    if (framePools.get(this.key) === this) framePools.delete(this.key);
  };

  function getFramePool(base, count) {
    var key = poolKey(base, count);
    var pool = framePools.get(key);
    if (!pool || pool.disposed) {
      pool = new FramePool(base, count);
      framePools.set(key, pool);
    }
    return pool;
  }

  function findMatchingDataElement(root, attribute, value) {
    var nodes = root.querySelectorAll('[' + attribute + ']');
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i].getAttribute(attribute) === value) return nodes[i];
    }
    return null;
  }

  function findSyncElements(root, key) {
    return Array.prototype.filter.call(root.querySelectorAll('[data-scrub-sync]'), function (element) {
      return (element.getAttribute('data-scrub-sync') || '').split(/\s+/).indexOf(key) !== -1;
    });
  }

  function canvasSignature(canvas) {
    return [
      canvas.getAttribute('data-scrub') || '',
      canvas.getAttribute('data-scrub-base') || '',
      canvas.getAttribute('data-scrub-count') || '',
      canvas.getAttribute('data-scrub-drive') || '',
      canvas.getAttribute('data-scrub-w0') || '',
      canvas.getAttribute('data-scrub-w1') || '',
      canvas.getAttribute('data-scrub-media') || ''
    ].join('|');
  }

  function Sequence(canvas) {
    this.canvas = canvas;
    this.root = canvas.closest('.shopify-section') || canvas.closest('section');
    this.key = canvas.getAttribute('data-scrub') || '';
    this.base = canvas.getAttribute('data-scrub-base') || '';
    this.count = parseInt(canvas.getAttribute('data-scrub-count'), 10);
    this.count = Number.isFinite(this.count) && this.count > 0 ? this.count : 1;
    this.drive = canvas.getAttribute('data-scrub-drive') || 'drop';
    if (!PROGRESS[this.drive]) this.drive = 'drop';
    this.window = [
      parseFloat(canvas.getAttribute('data-scrub-w0') || '0'),
      parseFloat(canvas.getAttribute('data-scrub-w1') || '1')
    ];
    this.media = canvas.getAttribute('data-scrub-media') || '';
    this.mediaQuery = this.media && window.matchMedia ? window.matchMedia(this.media) : null;
    this.signature = canvasSignature(canvas);
    this.poster = this.root ? findMatchingDataElement(this.root, 'data-scrub-poster', this.key) : null;
    this.syncElements = this.root ? findSyncElements(this.root, this.key) : [];
    this.context = canvas.getContext('2d');
    this.pool = null;
    this.pending = -1;
    this.lastDrawn = -1;
    this.active = false;
    this.destroyed = !this.root || !this.key || !this.base || !this.context;
    this.observed = false;
    this.idleTimer = 0;
    this.lastProgress = 0;

    canvas.style.opacity = '0';
    canvas.style.transition = 'opacity .5s ease';
  }

  Sequence.prototype.isBreakpointActive = function () {
    if (this.destroyed || !this.canvas.isConnected || !this.root.isConnected) return false;
    if (this.mediaQuery && !this.mediaQuery.matches) return false;
    return this.canvas.getClientRects().length > 0 && this.canvas.clientWidth > 0 && this.canvas.clientHeight > 0;
  };

  Sequence.prototype.isNear = function (viewportHeight) {
    if (!this.isBreakpointActive()) return false;
    if (rootProximity.get(this.root) === true) return true;
    var rect = this.root.getBoundingClientRect();
    return rect.top < viewportHeight * 2.5 && rect.bottom > -viewportHeight * 1.5;
  };

  Sequence.prototype.activate = function () {
    if (this.active || this.destroyed || reduced) return;
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = 0;
    }
    this.active = true;
    this.pending = -1;
    this.lastDrawn = -1;
    this.canvas.style.opacity = '0';
    this.pool = getFramePool(this.base, this.count);
    this.pool.acquire(this);
    this.size();
  };

  Sequence.prototype.deactivate = function () {
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = 0;
    }
    if (this.pool) this.pool.release(this);
    this.pool = null;
    this.active = false;
    this.pending = -1;
    this.lastDrawn = -1;
    this.canvas.style.opacity = '0';
    if (this.context && this.canvas.width && this.canvas.height) {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  };

  Sequence.prototype.setNear = function (near) {
    var self = this;
    if (near) {
      if (this.idleTimer) {
        window.clearTimeout(this.idleTimer);
        this.idleTimer = 0;
      }
      return;
    }
    if (!this.active || this.idleTimer) return;
    this.idleTimer = window.setTimeout(function () {
      self.idleTimer = 0;
      if (self.destroyed || !self.active) return;
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (!self.isNear(viewportHeight)) self.deactivate();
    }, SEQUENCE_IDLE_MS);
  };

  Sequence.prototype.size = function () {
    var width = this.canvas.clientWidth;
    var height = this.canvas.clientHeight;
    if (!width || !height) return false;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var backingWidth = Math.round(width * dpr);
    var backingHeight = Math.round(height * dpr);
    if (this.canvas.width !== backingWidth || this.canvas.height !== backingHeight) {
      this.canvas.width = backingWidth;
      this.canvas.height = backingHeight;
      this.lastDrawn = -1;
    }
    return true;
  };

  Sequence.prototype.sync = function (progress) {
    this.syncElements.forEach(function (element) {
      var range = element.getAttribute('data-scrub-range');
      if (range) {
        var parts = range.split(',');
        var start = parseFloat(parts[0]);
        var end = parseFloat(parts[1]);
        element.classList.toggle('is-on', progress >= start && progress < end);
        return;
      }
      var threshold = parseFloat(element.getAttribute('data-lit-at') || '0');
      element.classList.toggle('is-lit', progress >= threshold || element.hasAttribute('data-ao-editor-lit'));
    });
  };

  Sequence.prototype.draw = function () {
    if (!this.active || !this.pool || this.pending < 0 || !this.size()) return;
    var frameIndex = this.pool.nearest(this.pending);
    if (frameIndex < 0 || frameIndex === this.lastDrawn) return;
    var image = this.pool.frames[frameIndex];
    if (!image || !image.naturalWidth || !image.naturalHeight) return;

    var canvasWidth = this.canvas.width;
    var canvasHeight = this.canvas.height;
    var scale = Math.max(canvasWidth / image.naturalWidth, canvasHeight / image.naturalHeight);
    var drawWidth = image.naturalWidth * scale;
    var drawHeight = image.naturalHeight * scale;
    this.context.drawImage(
      image,
      (canvasWidth - drawWidth) / 2,
      (canvasHeight - drawHeight) / 2,
      drawWidth,
      drawHeight
    );
    this.lastDrawn = frameIndex;
    if (this.canvas.style.opacity !== '1') this.canvas.style.opacity = '1';
  };

  Sequence.prototype.update = function (viewportHeight) {
    if (!this.active || this.destroyed) return;
    if (!this.isBreakpointActive()) {
      this.deactivate();
      return;
    }

    var sectionProgress = PROGRESS[this.drive](this.root, viewportHeight);
    var start = Number.isFinite(this.window[0]) ? this.window[0] : 0;
    var end = Number.isFinite(this.window[1]) ? this.window[1] : 1;
    var localProgress = clamp((sectionProgress - start) / Math.max(0.001, end - start));
    var frameIndex = Math.round(localProgress * (this.count - 1));
    frameIndex = Math.max(0, Math.min(this.count - 1, frameIndex));

    this.lastProgress = localProgress;
    this.pending = frameIndex;
    this.sync(localProgress);
    this.pool.request(frameIndex);
    this.draw();
  };

  Sequence.prototype.onFrameReady = function () {
    if (this.active && !this.destroyed) schedule();
  };

  Sequence.prototype.resetForReducedMotion = function () {
    this.deactivate();
    this.syncElements.forEach(function (element) {
      element.classList.remove('is-lit');
      element.classList.remove('is-on');
    });
  };

  Sequence.prototype.destroy = function () {
    if (this.destroyed) return;
    this.deactivate();
    this.destroyed = true;
    unobserveSequence(this);
    this.syncElements.length = 0;
    this.poster = null;
    this.context = null;
    this.root = null;
    this.canvas = null;
  };

  function observeSequence(sequence) {
    if (sequence.observed || sequence.destroyed) return;
    sequence.observed = true;
    if (resizeObserver) resizeObserver.observe(sequence.canvas);
    if (intersectionObserver) {
      var count = observedRoots.get(sequence.root) || 0;
      observedRoots.set(sequence.root, count + 1);
      if (!count) intersectionObserver.observe(sequence.root);
    }
  }

  function unobserveSequence(sequence) {
    if (!sequence.observed) return;
    sequence.observed = false;
    if (resizeObserver && sequence.canvas) resizeObserver.unobserve(sequence.canvas);
    if (intersectionObserver && sequence.root) {
      var count = observedRoots.get(sequence.root) || 0;
      if (count <= 1) {
        intersectionObserver.unobserve(sequence.root);
        observedRoots.delete(sequence.root);
        rootProximity.delete(sequence.root);
      } else {
        observedRoots.set(sequence.root, count - 1);
      }
    }
  }

  function ensureObservers() {
    if (!resizeObserver && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(schedule);
    }
    if (!intersectionObserver && 'IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          rootProximity.set(entry.target, entry.isIntersecting);
        });
        schedule();
      }, { rootMargin: '150% 0px 150% 0px' });
    }
    sequenceRegistry.forEach(observeSequence);
  }

  function disconnectObservers() {
    if (resizeObserver) resizeObserver.disconnect();
    if (intersectionObserver) intersectionObserver.disconnect();
    resizeObserver = null;
    intersectionObserver = null;
    observedRoots.clear();
    rootProximity.clear();
    sequenceRegistry.forEach(function (sequence) {
      sequence.observed = false;
    });
  }

  function ensureRuntimeListeners() {
    if (runtimeController || reduced || !sequenceRegistry.size) return;
    runtimeController = new AbortController();
    var signal = runtimeController.signal;

    window.addEventListener('scroll', schedule, { passive: true, signal: signal });
    window.addEventListener('resize', schedule, { passive: true, signal: signal });
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true, signal: signal });
    document.addEventListener('visibilitychange', handleVisibilityChange, { signal: signal });
    ensureObservers();
  }

  function detachRuntimeListeners() {
    if (runtimeController) runtimeController.abort();
    runtimeController = null;
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    if (orientationTimer) window.clearTimeout(orientationTimer);
    orientationTimer = 0;
    disconnectObservers();
  }

  function handleOrientationChange() {
    schedule();
    if (orientationTimer) window.clearTimeout(orientationTimer);
    orientationTimer = window.setTimeout(schedule, 160);
  }

  function handleVisibilityChange() {
    if (!document.hidden) schedule();
  }

  function schedule() {
    if (reduced || document.hidden || animationFrame || !sequenceRegistry.size) return;
    animationFrame = window.requestAnimationFrame(run);
  }

  function run() {
    animationFrame = 0;
    cleanupDisconnected();
    if (reduced || !sequenceRegistry.size) return;

    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var sequences = Array.from(sequenceRegistry.values());
    var states = sequences.map(function (sequence) {
      var breakpointActive = sequence.isBreakpointActive();
      var near = breakpointActive && sequence.isNear(viewportHeight);
      sequence.setNear(near);
      return { sequence: sequence, breakpointActive: breakpointActive, near: near };
    });

    /* Acquire the newly visible side of a breakpoint before releasing the old
     * one. Pipette desktop/mobile can therefore hand off the shared frame pool
     * without an unnecessary decode cycle. */
    states.forEach(function (state) {
      if (state.near && !state.sequence.active) {
        state.sequence.activate();
        state.sequence.update(viewportHeight);
        state.justActivated = true;
      }
    });

    states.forEach(function (state) {
      if (!state.breakpointActive && state.sequence.active) state.sequence.deactivate();
      if (state.sequence.active && !state.justActivated) state.sequence.update(viewportHeight);
    });
  }

  function collectCanvases(scope) {
    var canvases = [];
    if (!scope) return canvases;
    if (scope.matches && scope.matches('canvas[data-scrub]')) canvases.push(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll('canvas[data-scrub]').forEach(function (canvas) {
        canvases.push(canvas);
      });
    }
    return canvases;
  }

  function destroySequence(sequence) {
    if (!sequence) return;
    if (sequence.canvas) sequenceRegistry.delete(sequence.canvas);
    sequence.destroy();
  }

  function cleanupDisconnected() {
    Array.from(sequenceRegistry.values()).forEach(function (sequence) {
      if (!sequence.canvas || !sequence.canvas.isConnected || !sequence.root || !sequence.root.isConnected) {
        destroySequence(sequence);
      }
    });
    if (!sequenceRegistry.size) detachRuntimeListeners();
  }

  function scan(scope) {
    cleanupDisconnected();
    collectCanvases(scope || document).forEach(function (canvas) {
      if (!canvas.getAttribute('data-scrub-base')) return;
      var current = sequenceRegistry.get(canvas);
      var signature = canvasSignature(canvas);
      if (current && current.signature === signature) return;
      if (current) destroySequence(current);

      var sequence = new Sequence(canvas);
      if (sequence.destroyed) return;
      sequenceRegistry.set(canvas, sequence);
      if (runtimeController) observeSequence(sequence);
    });

    ensureRuntimeListeners();
    ensureQuoteRotation();
    schedule();
  }

  function destroyScope(scope) {
    if (!scope) return;
    Array.from(sequenceRegistry.values()).forEach(function (sequence) {
      if (scope === sequence.canvas || (scope.contains && scope.contains(sequence.canvas))) {
        destroySequence(sequence);
      }
    });
    if (!sequenceRegistry.size) detachRuntimeListeners();
  }

  function destroyAllSequences() {
    Array.from(sequenceRegistry.values()).forEach(destroySequence);
    sequenceRegistry.clear();
    detachRuntimeListeners();
  }

  function disposeAllPools() {
    Array.from(framePools.values()).forEach(function (pool) {
      pool.dispose();
    });
    framePools.clear();
  }

  function handleReducedMotionChange(event) {
    reduced = !!event.matches;
    if (reduced) {
      if (quoteTimer) window.clearTimeout(quoteTimer);
      quoteTimer = 0;
      sequenceRegistry.forEach(function (sequence) {
        sequence.resetForReducedMotion();
      });
      disposeAllPools();
      detachRuntimeListeners();
      return;
    }
    ensureRuntimeListeners();
    ensureQuoteRotation();
    schedule();
  }

  /* Shared tap-state selector retained for the Oil botanical/Root interactions
   * and the Protocol active-system chips. */
  if (!window.__aoSelect) {
    window.__aoSelect = true;
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var button = target.closest('[data-ao-select]');
      if (!button) return;
      var host = button.closest('.shopify-section') || button.closest('section') || document.body;
      var index = button.getAttribute('data-ao-select');
      host.querySelectorAll('[data-ao-select]').forEach(function (option) {
        option.setAttribute('aria-pressed', option.getAttribute('data-ao-select') === index ? 'true' : 'false');
      });
      host.querySelectorAll('[data-ao-detail]').forEach(function (detail) {
        if (detail.getAttribute('data-ao-detail') === index) detail.removeAttribute('hidden');
        else detail.setAttribute('hidden', '');
      });
    });
  }

  function hasRotatingQuotes() {
    return Array.prototype.some.call(document.querySelectorAll('[data-ao-quotes]'), function (wrapper) {
      return wrapper.querySelectorAll('.ao-proof__quote').length > 1;
    });
  }

  function ensureQuoteRotation() {
    if (quoteTimer || reduced || !hasRotatingQuotes()) return;
    quoteTimer = window.setTimeout(function rotateQuotes() {
      quoteTimer = 0;
      if (reduced) return;
      document.querySelectorAll('[data-ao-quotes]').forEach(function (wrapper) {
        var items = wrapper.querySelectorAll('.ao-proof__quote');
        if (items.length < 2) return;
        var index = 0;
        items.forEach(function (item, itemIndex) {
          if (item.classList.contains('is-on')) index = itemIndex;
        });
        items[index].classList.remove('is-on');
        items[(index + 1) % items.length].classList.add('is-on');
      });
      ensureQuoteRotation();
    }, 5200);
  }

  function diagnostics() {
    return {
      version: VERSION,
      reducedMotion: reduced,
      listenerSetActive: !!runtimeController,
      animationFramePending: !!animationFrame,
      sequences: Array.from(sequenceRegistry.values()).map(function (sequence) {
        return {
          key: sequence.key,
          count: sequence.count,
          media: sequence.media,
          active: sequence.active,
          pending: sequence.pending,
          drawn: sequence.lastDrawn
        };
      }),
      pools: Array.from(framePools.values()).map(function (pool) {
        return {
          base: pool.base,
          count: pool.count,
          subscribers: pool.subscribers.size,
          loaded: pool.states.filter(function (state) { return state === 2; }).length,
          failures: Array.from(pool.failures)
        };
      })
    };
  }

  window.AfoScrub = {
    version: VERSION,
    scan: scan,
    destroy: destroyScope,
    refresh: schedule,
    diagnostics: diagnostics
  };

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyScope(event.target);
  });
  document.addEventListener('shopify:section:reorder', function () {
    scan(document);
  });
  document.addEventListener('shopify:section:select', function (event) {
    scan(event.target || document);
  });
  // Theme Editor: a selected scroll-lit block (Root-to-Strand callout) stays lit while it is being edited.
  document.addEventListener('shopify:block:select', function (event) {
    var el = event.target && event.target.closest ? event.target.closest('[data-scrub-sync][data-lit-at]') : null;
    if (el) { el.setAttribute('data-ao-editor-lit', ''); el.classList.add('is-lit'); }
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var el = event.target && event.target.closest ? event.target.closest('[data-ao-editor-lit]') : null;
    if (!el) return;
    el.removeAttribute('data-ao-editor-lit');
    schedule();
  });

  window.addEventListener('pagehide', function () {
    if (quoteTimer) window.clearTimeout(quoteTimer);
    quoteTimer = 0;
    destroyAllSequences();
    disposeAllPools();
  });
  window.addEventListener('pageshow', function () {
    scan(document);
  });

  if (reducedQuery) {
    if (typeof reducedQuery.addEventListener === 'function') {
      reducedQuery.addEventListener('change', handleReducedMotionChange);
    } else if (typeof reducedQuery.addListener === 'function') {
      reducedQuery.addListener(handleReducedMotionChange);
    }
  }

  // Every sequence lives below the first screen (ao-convergence, the formula wheel, aocdx-why). Measuring
  // them at DOMContentLoaded forces layout before the first paint; start after the load event instead.
  function startScan() { scan(document); }
  if (document.readyState === 'complete') startScan();
  else window.addEventListener('load', startScan, { once: true });
})();
